import { Box, Button, Link as MuiLink, Skeleton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMemo, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Clapperboard } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useCan } from "../../auth/permissions.js";
import { listAllDocs, listCategories, updateCategory } from "./api.js";
import { DocSearchBox } from "./DocSearchBox.js";
import { CategoryRow } from "./doc-sidebar/CategoryRow.js";
import { buildTree, pickActiveSelection, type CategoryNode } from "./doc-sidebar/tree.js";
import { EditCategoryDialog } from "./EditCategoryDialog.js";
import { MAX_FOLDER_DEPTH } from "./folderOptions.js";
import { NewCategoryModal } from "./NewCategoryModal.js";

/**
 * Folder navigation for `/documentation/*` — THE DOCUMENTATION IS THE
 * LIBRARY SHELF (2026-09-14).
 *
 * One leaf row per folder — use cases, FAQs, videos, everything not tied to
 * a version — curated by folder publication + the Access page. The folder
 * rows keep their management controls (eye, rename).
 *
 * Release documentation (a document pinned to a version) is deliberately
 * NOT here. It is read under its version on CRM ▸ Releases, which is where
 * it is uploaded, and the tree used to repeat that page as a second
 * "Releases" root — product → release → version — one click away. Two
 * places listing the same guides was what made the section hard to read;
 * a pinned document still opens in the reader and still carries its
 * version chip, it is just not listed twice.
 *
 * The data it joins: categories, and the COMPLETE document list via
 * `listAllDocs` (paginates past the server's 100/page cap — a single capped
 * page under-counted every folder once the library passed 100). The pure
 * tree/URL logic lives in `doc-sidebar/tree.ts`.
 */
export function DocumentationSidebar(): JSX.Element {
  const location = useLocation();
  const { categorySlug, libraryDocSlug } = pickActiveSelection(location.pathname);

  const canEdit = useCan("documentation:edit");
  // Separate permission on purpose: authoring a document and deciding which
  // customer may read it are different jobs, and the roles matrix hands them
  // out independently.
  const canManageAccess = useCan("documentation:manage_access");
  // Online Training (reseller video courses) lives in this section too.
  const canView = useCan("documentation:view");
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  // The folder a "+ sub-folder" click targets; null = a new top-level folder.
  const [createParentId, setCreateParentId] = useState<number | null>(null);
  // Folders the operator collapsed. Sub-folders show by default so a new
  // one is visible the moment it is created.
  const [collapsed, setCollapsed] = useState<Set<number>>(() => new Set());

  // Inline category management, right where the operator browses — without
  // it a hidden category could never be un-hidden and a name never edited
  // from the surface staff actually land on. (Reported 2026-05-20.)
  const [pendingEdit, setPendingEdit] = useState<{
    id: number;
    name: string;
    visible: boolean;
    visibleToResellers: boolean;
  } | null>(null);

  const visibilityMutation = useMutation({
    mutationFn: ({ id, visible }: { id: number; visible: boolean }) =>
      updateCategory(id, {
        document_category: { visible_in_customer_portal: visible },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["docs", "categories"] });
    },
  });

  const { data: cats, isLoading: catsLoading } = useQuery({
    queryKey: ["docs", "categories"],
    queryFn: listCategories,
  });

  const { data: allDocs, isLoading: docsLoading } = useQuery({
    queryKey: ["docs", "tree", "all"],
    queryFn: () => listAllDocs({ q: "", category_id: "all" }),
  });

  const loading = catsLoading || docsLoading;

  const tree = useMemo(() => buildTree(cats?.data ?? [], allDocs ?? []), [cats, allDocs]);

  // For deep links: the folder the open document lives in, so the tree
  // shows the reader their location. A release document has no folder row
  // to highlight — its home is the version page.
  const docCategorySlug = useMemo<string | null>(() => {
    if (!libraryDocSlug) return null;
    const doc = (allDocs ?? []).find((d) => d.slug === libraryDocSlug);
    if (!doc || doc.default_release) return null;
    return doc.category?.slug ?? "uncategorized";
  }, [libraryDocSlug, allDocs]);

  const focusedCategorySlug = categorySlug ?? docCategorySlug;

  const renderRow = (
    node: CategoryNode,
    depth: number,
    opts: { leaf: boolean; expanded: boolean; onToggle: () => void; onRequestAddChild?: () => void },
  ): JSX.Element => (
    <CategoryRow
      key={node.slug}
      name={node.name}
      count={node.count}
      hidden={node.hiddenInPortal}
      depth={depth}
      leaf={opts.leaf}
      expanded={opts.expanded}
      focused={focusedCategorySlug === node.slug}
      onToggle={opts.onToggle}
      // The name navigates to the folder; it must not collapse it too.
      onNameToggle={() => undefined}
      navigateTo={`/documentation/type/${encodeURIComponent(node.slug)}`}
      canManage={canEdit && node.id != null}
      visible={!node.hiddenInPortal}
      toggling={visibilityMutation.isPending && visibilityMutation.variables?.id === node.id}
      onToggleVisibility={(next) => {
        if (node.id != null) visibilityMutation.mutate({ id: node.id, visible: next });
      }}
      onRequestEdit={() => {
        if (node.id != null) {
          setPendingEdit({
            id: node.id,
            name: node.name,
            visible: !node.hiddenInPortal,
            visibleToResellers: node.visibleToResellers,
          });
        }
      }}
      {...(opts.onRequestAddChild ? { onRequestAddChild: opts.onRequestAddChild } : {})}
    />
  );

  // One folder row plus, when it is open, its sub-folders — recursively, so
  // "Videos ▸ R11.0 ▸ Command Control" shows all three tiers.
  const renderBranch = (node: CategoryNode, depth: number): JSX.Element[] => {
    const hasChildren = node.children.length > 0;
    const open = node.id == null || !collapsed.has(node.id);
    const toggle = (): void => {
      if (node.id == null) return;
      const id = node.id;
      setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    };
    const rows = [renderRow(node, depth, {
      leaf: !hasChildren,
      expanded: hasChildren && open,
      onToggle: toggle,
      // The deepest tier takes no sub-folders of its own.
      ...(canEdit && node.id != null && depth < MAX_FOLDER_DEPTH
        ? { onRequestAddChild: () => { setCreateParentId(node.id); setCreateOpen(true); } }
        : {}),
    })];
    if (hasChildren && open && depth < MAX_FOLDER_DEPTH + 1) {
      for (const child of node.children) rows.push(...renderBranch(child, depth + 1));
    }
    return rows;
  };

  if (loading) {
    return (
      <Box sx={{ display: "grid", gap: 1 }}>
        <Box sx={{ px: 0.5 }}>
          <DocSearchBox />
        </Box>
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} variant="rounded" sx={{ height: 28 }} />
        ))}
      </Box>
    );
  }

  const nothing = tree.library.length === 0;

  return (
    <Box component="nav" aria-label="Documentation folders" sx={{ display: "grid", gap: 0.25, fontSize: 14 }}>
      {/* Search the WHOLE catalogue (both shelves) — always at the top so it
        * is reachable from any reader surface the sidebar mounts on. */}
      <Box sx={{ px: 0.5, pb: 1 }}>
        <DocSearchBox />
      </Box>
      {canEdit && (
        <Box sx={{ display: "grid", gap: 1, px: 0.5, pb: 0.75 }}>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={() => {
              setCreateParentId(null);
              setCreateOpen(true);
            }}
            sx={{ width: "100%", justifyContent: "center", bgcolor: "action.hover" }}
          >
            + New category
          </Button>
          <MuiLink
            component={RouterLink}
            to="/documentation/categories"
            underline="hover"
            sx={{ textAlign: "center", fontSize: "11px", color: "text.disabled", "&:hover": { color: "text.primary" } }}
          >
            Manage categories
          </MuiLink>
        </Box>
      )}
      {canView && (
        <Box sx={{ px: 0.5, pb: 0.75 }}>
          <Button
            component={RouterLink}
            to="/documentation/online-training"
            variant="text"
            color="inherit"
            size="small"
            startIcon={<Clapperboard size={16} />}
            data-testid="docs-sidebar-online-training-link"
            sx={{ width: "100%", justifyContent: "flex-start", fontWeight: 500 }}
          >
            Online Training
          </Button>
        </Box>
      )}
      {canManageAccess && (
        <Box sx={{ display: "grid", gap: 1, px: 0.5, pb: 0.75 }}>
          <MuiLink
            component={RouterLink}
            to="/documentation/access"
            underline="hover"
            data-testid="docs-sidebar-access-link"
            sx={{ textAlign: "center", fontSize: "11px", color: "text.disabled", "&:hover": { color: "text.primary" } }}
          >
            Who sees what
          </MuiLink>
        </Box>
      )}

      {tree.library.length > 0 && (
        <>
          <SectionLabel>Folders</SectionLabel>
          {tree.library.flatMap((node) => renderBranch(node, 0))}
        </>
      )}

      {nothing && (
        <Box
          sx={{
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.4),
            p: 1.5,
            fontSize: 12,
            color: "text.disabled",
          }}
        >
          {canEdit ? (
            <>
              No folders yet. Use{" "}
              <Box component="span" sx={{ fontWeight: 500 }}>+ New category</Box> above to
              create your first folder.
            </>
          ) : (
            "No folders yet. Ask an admin to create one."
          )}
        </Box>
      )}

      {canEdit && (
        <NewCategoryModal
          open={createOpen}
          parentId={createParentId}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            void qc.invalidateQueries({ queryKey: ["docs", "categories"] });
          }}
        />
      )}

      {canEdit && (
        <EditCategoryDialog
          target={pendingEdit}
          onClose={() => setPendingEdit(null)}
          onSaved={() => {
            setPendingEdit(null);
            void qc.invalidateQueries({ queryKey: ["docs", "categories"] });
          }}
        />
      )}
    </Box>
  );
}

function SectionLabel({ children }: { children: string }): JSX.Element {
  return (
    <Box
      sx={{
        px: 1,
        pt: 1,
        pb: 0.5,
        fontSize: "10px",
        fontWeight: 500,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: "text.disabled",
      }}
    >
      {children}
    </Box>
  );
}
